import { Injectable, OnDestroy } from '@angular/core';
import { datadogLogs, LogsEvent, LogsEventDomainContext } from '@datadog/browser-logs';
import { datadogRum, RumEvent, RumEventDomainContext, RumResourceEvent } from '@datadog/browser-rum';
import { environment } from '../../environments/environment';
import { User } from '@app/models/user';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DatadogService implements OnDestroy {
    private readonly subscriptions = new Subscription();
    private readonly tokenSearch: RegExp = /(access_token|code)/;

    constructor(private readonly authService: AuthenticationService) {}

    init() {
        if (environment.datadog?.enabled) {
            this.subscriptions.add(
                this.authService.currentUser.subscribe(user => {
                    if (user?.id) {
                        this.assignUser(user);
                    } else {
                        this.unassignUser();
                    }
                })
            );

            datadogRum.init({
                applicationId: environment.datadog.applicationId,
                clientToken: environment.datadog.clientToken,
                site: 'datadoghq.eu',
                service: environment.datadog.service,
                env: environment.datadog.environment,
                version: environment.version,
                sessionSampleRate: 100,
                allowedTracingUrls: [/^https:\/\/api[^/]+\.everlightradiology\.com/],
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'mask-user-input',
                beforeSend: (event: RumEvent, context: RumEventDomainContext) => this.scrubAndFilterRum(event, context)
            });

            datadogLogs.init({
                clientToken: environment.datadog.clientToken,
                site: 'datadoghq.eu',
                service: environment.datadog.service,
                env: environment.datadog.environment,
                version: environment.version,
                forwardErrorsToLogs: true,
                forwardConsoleLogs: 'all',
                sessionSampleRate: 100,
                beforeSend: (event: LogsEvent, context: LogsEventDomainContext) => this.scrubAndFilterLog(event, context)
            });
        }
    }

    assignUser(user: User) {
        datadogRum.setUser({
            id: user.id,
            name: user.username
        });
        datadogLogs.setUser({
            id: user.id,
            name: user.username
        });
    }

    unassignUser() {
        datadogRum.clearUser();
        datadogLogs.clearUser();
    }

    scrubAndFilterLog(event: LogsEvent, _: LogsEventDomainContext): boolean {
        if (event.view.url.includes('callback') === true) {
            return false;
        }

        if ((event.http?.url ?? '').search(this.tokenSearch) > -1) {
            event.http!.url = this.scrubToken(event.http!.url);
        }

        if ((event.view?.url ?? '').search(this.tokenSearch) > -1) {
            event.view!.url = this.scrubToken(event.view!.url);
        }

        return true;
    }

    scrubAndFilterRum(event: RumEvent, _: RumEventDomainContext): boolean {
        if (event.view.url.includes('callback') === true) {
            return false;
        }

        if (event.view.url.search(this.tokenSearch) > -1) {
            event.view.url = this.scrubToken(event.view.url);
        }

        if (this.isResourceEvent(event) && event.resource.url?.search(this.tokenSearch) > -1) {
            event.resource.url = this.scrubToken(event.resource.url);
        }

        return true;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private scrubToken(content: string): string {
        return content.replace(/(access_token|code)=[^&\s]+/gi, '$1=******');
    }

    private isResourceEvent(event: RumEvent): event is RumResourceEvent {
        return event.type === 'resource';
    }
}
